import { Text } from '../ui/Typography.js'
import { RelativeTime } from './RelativeTime.js'
import { Stack } from '../layout/Stack.js'
import { EmptyState } from './EmptyState.js'
import { ErrorState } from '../feedback/ErrorState.js'
import { Skeleton } from '../ui/Skeleton.js'

export interface TimelineEntry {
  id: string
  /** What happened, in words. "Paused the listing", not "provider.status". */
  what: React.ReactNode
  /**
   * Who did it. `null` for something the platform did by itself, and
   * **omitted** where the question does not arise.
   *
   * Both used to read *"The platform"*, so every row of 9.1's attention
   * panel — a refused card, an invitation that bounced, a category that
   * filled up — was attributed to the platform as though something had done
   * them. Nothing did them; they are facts.
   */
  who?: string | null
  /**
   * When, as an instant. Drawn as *"3 hours ago"* with the exact time on hover.
   *
   * This was printed straight into the row, so every timeline on the desk read
   * `2026-09-20T17:53:35.233Z`. The audit log, 9.1's attention panel and a
   * class's moderation history all did it, and the attention panel's own
   * comment said *"RelativeTime says 'Never' for nothing"* about a component
   * that was never being called.
   */
  when: string
  /** A reason, where one was required — break-glass access, a cancellation. */
  detail?: React.ReactNode
}

/**
 * What happened, most recent first.
 *
 * The audit log, and a record's own history. Every row names a person or says
 * plainly that nobody was involved: *"the platform"* is an answer and a blank is
 * not, and half these rows exist precisely so somebody can be asked about them.
 */
export function Timeline({
  entries,
  empty,
  loading,
  loadingRows = 3,
  error,
  onRetry,
}: {
  /** `undefined` means it has not arrived. An empty array means there is nothing. */
  entries: TimelineEntry[] | undefined
  empty?: string
  loading?: boolean
  loadingRows?: number
  /**
   * The query failed.
   *
   * Worse here than on a table. `!entries` draws the skeleton, and a failed
   * query leaves `entries` undefined for ever — so the audit log spun its
   * loading bars indefinitely and nothing ever said the request had not
   * worked. A skeleton is a promise that something is coming.
   */
  error?: boolean
  onRetry?: () => void
}) {
  /**
   * The shape while it loads, for the same reason `DataTable` draws one.
   *
   * This sits beside a table on every record screen. A table that skeletons next
   * to a history that renders nothing is a panel that jumps when the second
   * request lands — which is precisely what the table's skeleton exists to avoid,
   * undone by the component next to it.
   */
  if (!loading && error) {
    return <ErrorState {...(onRetry ? { onRetry } : {})} />
  }

  if (loading || !entries) {
    return (
      <ol className="relative ml-2 border-l border-hairline" aria-busy="true">
        {Array.from({ length: loadingRows }, (_, i) => (
          <li key={i} className="relative py-3 pl-6">
            <span
              className="absolute -left-[5px] top-5 size-2.5 rounded-full bg-hairline"
              aria-hidden
            />
            <Skeleton className="h-4 w-2/3" />
          </li>
        ))}
      </ol>
    )
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        illustration="activity"
        title="Nothing recorded yet"
        description={empty ?? 'Anything that happens here will be listed, newest first.'}
      />
    )
  }
  return (
    <ol className="relative ml-2 border-l border-hairline">
      {entries.map((e) => (
        <li key={e.id} className="relative py-3 pl-6">
          <span
            className="absolute -left-[5px] top-5 size-2.5 rounded-full bg-hairline-strong"
            aria-hidden
          />
          <Stack gap={1}>
            <Text variant="body">{e.what}</Text>
            <Text variant="caption">
              {e.who !== undefined && <>{e.who ?? 'The platform'} · </>}
              <RelativeTime iso={e.when} never="–" />
            </Text>
            {e.detail && <Text variant="secondary">{e.detail}</Text>}
          </Stack>
        </li>
      ))}
    </ol>
  )
}
